import './contentColumnControl.scss';
import React from 'react';
import {BaseCol} from './base-col';
import {ColumnControlProps} from '../search-result-item-types';
import {DeepReadonly} from 'utility-types';
import {HibuTag, ProdDocument} from '../../../../search-types';
import {formatBytes, formatDateShort} from '../../../../../utils/utils';
import {_DeepReadonlyArray} from 'utility-types/dist/mapped-types';
import {t} from 'i18next';

const METADATA_FIELD = 'metadata.';
const TAG_FIELD = 'tags.';
const DATE_FIELD_ENDING = '_date';
const MAX_NUMBER_ARRAY_ELEMENTS = 5;
const MAX_LENGTH = 120;
export const ContentColumnControl = ({doc, resultItemViewConfig}: ColumnControlProps): React.JSX.Element => {

    function joinFieldValues(doc: DeepReadonly<ProdDocument>, fieldValues: string[], fieldSeparator: string): string {
        return fieldValues
            .map((field: string): string => {
                if (field.startsWith(METADATA_FIELD)) {
                    return extractMetadata(field);
                } else if (field.startsWith(TAG_FIELD)) {
                    return extractTag(field);
                } else {
                    return doc[field];
                }
            })
            .filter(value => value != null && value !== '')
            .map(value => shorten(value, MAX_LENGTH))
            .join(fieldSeparator);
    }

    function extractMetadata(field: string) {
        let value = doc.metadata[field.substring(METADATA_FIELD.length)];
        if (Array.isArray(value)) {
            if (value.length > MAX_NUMBER_ARRAY_ELEMENTS) {
                const last = value[value.length - 1];
                return value.slice(0, MAX_NUMBER_ARRAY_ELEMENTS - 1).join(resultItemViewConfig.mainSeparator) + '... ' + last;
            } else {
                value = value.join(resultItemViewConfig.mainSeparator);
            }
        } else if (value && field.endsWith(DATE_FIELD_ENDING)) {
            value = formatDateShort(value);
        } else if (field === 'metadata.filesize_long') {
            value = formatBytes(value, t);
        } else if (field === 'metadata.numberOfPages_long') {
            if (value === 1) {
                value = value + ' ' + t('fieldTitle.metadata.numberOfPages_long.unit.singular');
            } else if (value !== undefined) {
                value = value + ' ' + t('fieldTitle.metadata.numberOfPages_long.unit.plural');
            }
        }
        return value;
    }

    function extractTag(field: string): string {
        const hibuTags: _DeepReadonlyArray<HibuTag> = doc?.hibuTags[field.substring(TAG_FIELD.length)];
        let value = '';
        if (hibuTags) {
            if (field.endsWith(DATE_FIELD_ENDING)) {
                value = formatDateShort(hibuTags[0].label);
            } else if (hibuTags.length > MAX_NUMBER_ARRAY_ELEMENTS) {
                const last = hibuTags[hibuTags.length - 1];
                value = hibuTags.slice(0, MAX_NUMBER_ARRAY_ELEMENTS - 1).map(v => v.label).join(resultItemViewConfig.mainSeparator) + '... ' + last.label;
            } else {
                value = hibuTags.map(v => v.label).join(resultItemViewConfig.mainSeparator);
            }
        }
        return value;
    }

    const shorten = (s: string, maxLength: number) => {
        return (s && s.length > maxLength) ? s.substring(0, maxLength) + '...' : s;
    };

    const getTitle = (doc: DeepReadonly<ProdDocument>) => {
        const title = joinFieldValues(doc, resultItemViewConfig.title, resultItemViewConfig.titleSeparator);
        return <p dangerouslySetInnerHTML={{__html: title}}/>;
    };

    const getMain = (doc: DeepReadonly<ProdDocument>) => {
        const main = joinFieldValues(doc, resultItemViewConfig.main, resultItemViewConfig.mainSeparator);
        return <p dangerouslySetInnerHTML={{__html: main}}/>;
    };

    const getSub = (doc: DeepReadonly<ProdDocument>) => {
        const sub = joinFieldValues(doc, resultItemViewConfig.sub, resultItemViewConfig.subSeparator);
        return <p dangerouslySetInnerHTML={{__html: sub}}/>;
    };

    return (
        <BaseCol className={'document-column-content'}>
            <div className={'document-title'}>{getTitle(doc)}</div>
            <div className={'document-sub'}>{getSub(doc)}</div>
            <div className={'document-main'}>{getMain(doc)}</div>
        </BaseCol>
    );
};
